import { Typography } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { CUSTOM_PLAN_DATA } from '@/widgets/pricing/pricingCards/lib/constants';
import { getFormattedPlanPrice } from '@/widgets/pricing/pricingCards/lib/utils/getFormattedPlanPrice';

import { PaymentInterval, Plan, PlanCategory, Price as PriceType } from '@/shared/types/Billing';

import { PRICING_CARD_DISCOUNT } from '../../constants/pricingCardDiscound';
import { MinutesText } from '../MinutesText';

type PriceBlockProps = {
  minutesValue: number | string;
  plan: Plan;
  selectedPaymentPeriod: PaymentInterval;
};

export const PriceBlock = ({ minutesValue, plan, selectedPaymentPeriod }: PriceBlockProps) => {
  const { category } = plan;
  const isEnterprise = category === PlanCategory.Enterprise;
  const isCustomPlan = plan.id === CUSTOM_PLAN_DATA.id || isEnterprise;

  const priceColor = isEnterprise ? 'common.white' : 'grey.900';
  const mutedColor = isEnterprise ? 'grey.300' : 'grey.500';

  const activePrices = plan.prices.filter((price) => price.status === 'active');
  const findPrice = (interval: PaymentInterval): PriceType | undefined =>
    activePrices.find((price) => price.recurring?.interval === interval);

  const monthlyPrice = findPrice(PaymentInterval.Month);
  const yearPrice = findPrice(PaymentInterval.Year);
  const isYearlySelected = selectedPaymentPeriod === PaymentInterval.Year;

  // Effective per-month price for the selected billing period.
  const perMonth =
    isYearlySelected && yearPrice
      ? getFormattedPlanPrice({ paymentInterval: PaymentInterval.Year, price: yearPrice.unit_amount })
      : getFormattedPlanPrice({
          paymentInterval: PaymentInterval.Month,
          price: (monthlyPrice ?? yearPrice)?.unit_amount ?? 0,
        });

  // RASK35 promo (Creator, Creator Pro, yearly only): the card advertises the price after the
  // promo code; the code itself is applied by the user at checkout (see DiscountChip's asterisk).
  const discountConfig = PRICING_CARD_DISCOUNT[category as keyof typeof PRICING_CARD_DISCOUNT];
  const promoUnitAmount =
    isYearlySelected && discountConfig && yearPrice
      ? Math.round(yearPrice.unit_amount * (1 - discountConfig.year))
      : undefined;
  const promoPerMonth =
    promoUnitAmount === undefined
      ? undefined
      : getFormattedPlanPrice({ paymentInterval: PaymentInterval.Year, price: promoUnitAmount });

  // With the promo, the undiscounted annual per-month price becomes the strikethrough.
  const showStrike = promoPerMonth !== undefined && promoPerMonth < perMonth;
  const displayPerMonth = promoPerMonth ?? perMonth;

  const annualTotal = yearPrice ? Math.round((promoUnitAmount ?? yearPrice.unit_amount) / 100) : undefined;

  // Figma: annual shows the yearly total, monthly shows a plain "billed monthly" line.
  const getBilledLabel = () => {
    if (!isYearlySelected) return 'billed monthly';
    if (annualTotal) return `billed $${annualTotal} yearly`;

    return undefined;
  };
  const billedLabel = getBilledLabel();

  return (
    <Stack gap={12}>
      <Stack gap={4}>
        <Stack alignItems="flex-end" direction="row" gap={4}>
          {isCustomPlan ? (
            <Typography color={priceColor} variant="title-2-sb">
              Custom
            </Typography>
          ) : (
            <>
              {showStrike && (
                <Typography color="grey.300" sx={{ textDecoration: 'line-through' }} variant="title-4-md">
                  {`$${perMonth}`}
                </Typography>
              )}
              <Typography color={priceColor} variant="title-2-sb">
                {`$${displayPerMonth}`}
              </Typography>
              <Typography color={mutedColor} sx={{ paddingBottom: 2 }} variant="body-2-md">
                /mo
              </Typography>
            </>
          )}
        </Stack>
        {!isCustomPlan && billedLabel ? (
          <Typography color={mutedColor} variant="caption-1-md">
            {billedLabel}
          </Typography>
        ) : (
          <Box sx={{ height: 16 }} />
        )}
      </Stack>
      <MinutesText category={category} value={minutesValue} />
    </Stack>
  );
};
